<template>
  <div :style="{ width: canvasWidth + 'px', height: height }">
    <canvas :ref="barRef"></canvas>
  </div>
</template>

<script>
import { Chart, registerables } from "chart.js";
Chart.register(...registerables);

export default {
  props: {
    barRef: { type: String, default: "bar" },
    width: { type: String, default: "700px" },
    height: { type: String, default: "500px" },
    labels: { type: Array },
    datasetLabel: { type: String },
    dataList: { type: Array },
    backgroundColor: {
      type: String || Array,
      default: "rgba(217, 217, 217, 1)",
    },
    borderColor: { type: String || Array, default: "rgba(217, 217, 217, 1)" },
    borderWidth: { type: Number, default: 1 },
    title: { type: String },
    quantityPlanTotalTon: { type: Number }, // 실행수량
    pointGap: { type: Number, default: 60 }, // 꼭짓점 간 간격
  },
  data() {
    return {
      chartInstance: null,
      chartOptions: {
        // indexAxis: "y",
        responsive: true,
        maintainAspectRatio: false,
        hover: {
          mode: "nearest", // 가장 가까운 포인트에만 반응
          animationDuration: 0, // 즉시 반응 (딜레이 없애기)
        },
        interaction: {
          mode: "nearest", // 가장 가까운 포인트에 반응
          intersect: false, // 포인트와 직접 겹치지 않아도 반응
        },
        layout: {
          padding: {
            left: 19,
            top: 30, // 차트상단 여백 추가 - 값이 안 잘리게 함
          },
        },
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            enabled: true,
            position: "average", // 중앙 정렬
            caretPadding: 20, // 툴팁과 데이터 포인트 사이 여백
            displayColors: false, // 색상 표시 제거
            callbacks: {
              label: (context) => `반입량 누계: ${context.raw}`,
            },
          },
        },
        scales: {
          x: {
            offset: true,
            beginAtZero: true,
            grid: { display: false, color: "#F2F2F2", drawBorder: false },
            padding: {
              left: 30,
            },
            ticks: {
              color: "#5E6C83", // X축 색상 조정
            },
          },
          y: {
            min: 0,
            beginAtZero: true,
            ticks: {
              padding: 12, // 값과 축 간격을 조정하여 더 잘 보이게 함
              color: "#5E6C83",
            },
            afterFit: function(scaleInstance) {
              scaleInstance.width = 60; // 맨 왼쪽 y축 width 강제 고정
            },
            grid: {
              color: "#F2F2F2",
              // drawBorder: false,
            },
          },
        },
      },
    };
  },
  watch: {
    dataList(newVal) {
      if (newVal) {
        if (this.chartInstance) {
          this.chartInstance.destroy(); // 기존 차트 제거
        }
        this.$nextTick(() => {
          this.createBarChart(); // 새 데이터로 차트 다시 그리기
        });
      }
    },
  },
  computed: {
    canvasWidth() {
      return this.labels?.length
        ? this.labels.length * this.pointGap + 120
        : 600;
    },
  },
  async mounted() {
    this.createBarChart();
  },
  methods: {
    createBarChart() {
      const ctx = this.$refs[this.barRef].getContext("2d");

      const options = JSON.parse(JSON.stringify(this.chartOptions));
      const chartData = this.dataList || [];
      const dataMax = Math.max(
        ...chartData.map(Number).filter(Number.isFinite),
        0,
      );
      const axisBaseValue = Math.max(
        Number(this.quantityPlanTotalTon) || 0,
        dataMax,
      );
      const quantityPlanTotal = Number(this.quantityPlanTotalTon) || 0;
      const percentList = chartData.map((data) => {
        if (quantityPlanTotal <= 0) return "0.00";

        return ((Number(data) / quantityPlanTotal) * 100).toFixed(2);
      });

      if (axisBaseValue > 0) {
        const stepSize = this.getStepSize(axisBaseValue);
        const byStep = Math.ceil(axisBaseValue / stepSize) * stepSize;

        // 최댓값이 눈금에 정확히 맞으면 라벨 표시를 위해 한 칸 여유를 둔다.
        const adjustedMax =
          byStep === axisBaseValue ? axisBaseValue + stepSize : byStep;

        options.scales.y.max = adjustedMax;
        // const maxYLine = Math.ceil(this.quantityPlanTotalTon * 1.1);
        // options.scales.y.suggestedMax = maxYLine;
      }

      // ✅ 그라데이션 정의
      const gradient = ctx.createLinearGradient(0, 0, 0, ctx.canvas.height);
      gradient.addColorStop(0, "rgba(33, 150, 243, 0.3)"); // 상단 (연한 파랑)
      gradient.addColorStop(0.5, "rgba(33, 150, 243, 0.15)"); // 중간 (더 연한 파랑)
      gradient.addColorStop(1, "rgba(33, 150, 243, 0.03)"); // 하단 (거의 투명)

      this.chartInstance = new Chart(this.$refs[this.barRef], {
        type: "line",
        data: {
          labels: this.formattedLabelslist(this.labels),
          datasets: [
            {
              barPercentage: 0.2,
              label: this.datasetLabel,
              data: this.dataList,
              // backgroundColor: this.backgroundColor,
              borderColor: this.borderColor,
              borderWidth: this.borderWidth,
              pointStyle: "circle",
              pointHoverRadius: 5,
              pointHitRadius: 40,
              // 아래 영역 그라데이션 채우기
              fill: { target: "origin", above: gradient },
              backgroundColor: gradient, // 그라데이션 채우기
              tension: 0.4, // 부드러운 곡선
              pointBackgroundColor: this.borderColor,
              customLabels: percentList,
            },
          ],
        },
        options: options,
        plugins: [
          {
            id: "dataLabels", // 꼭짓점 위 데이터 값 표시
            afterDatasetsDraw(chart) {
              const ctx = chart.ctx;
              chart.data.datasets.forEach((dataset, datasetIndex) => {
                const meta = chart.getDatasetMeta(datasetIndex);
                const customLabels = dataset.customLabels || [];

                meta.data.forEach((point, index) => {
                  const value =
                    customLabels[index] !== undefined
                      ? customLabels[index] + "%"
                      : "";
                  if (!value) return;

                  // const value = dataset.data[index] + "%";
                  ctx.fillStyle = "#222222";
                  ctx.font = "12px Arial";
                  ctx.textAlign = "center";
                  ctx.fillText(value, point.x, point.y - 10);
                });
              });
            },
          },
          {
            id: "verticalLineOnHover", // 호버시 y축선 표시
            beforeDatasetsDraw(chart) {
              if (
                !chart.tooltip ||
                !chart.tooltip._active ||
                chart.tooltip._active.length === 0
              ) {
                return;
              }

              const ctx = chart.ctx;
              const activePoint = chart.tooltip._active[0]; // 호버 중인 포인트
              if (!activePoint) return;

              const x = activePoint.element.x;
              const topY = chart.scales.y.top;
              const bottomY = chart.scales.y.bottom;

              ctx.save();
              ctx.beginPath();
              ctx.moveTo(x, topY);
              ctx.lineTo(x, bottomY);
              ctx.lineWidth = 1;
              ctx.strokeStyle = "#edf3ff";
              ctx.stroke();
              ctx.restore();
            },
          },
        ],
      });
    },
    /** 라벨 포맷팅 */
    formattedLabelslist(list) {
      const formatted = list.map((item) => {
        const [year, month] = item.split(".");
        const yy = year.slice(-2); // 마지막 두 자리
        return `${yy}년 ${month}월`;
      });
      return formatted;
    },
    /** y축 stepSize 계산 */
    getStepSize(value) {
      if (value <= 10) return 2;
      if (value <= 20) return 5;
      if (value <= 50) return 10;
      if (value <= 100) return 20;
      if (value <= 500) return 50;
      if (value <= 1000) return 100;
      if (value <= 5000) return 200;
      if (value <= 10000) return 500;
      if (value <= 50000) return 1000;
      if (value <= 100000) return 2000;
      if (value <= 200000) return 5000;
      return 10000;
    },
  },
};
</script>

<style lang="css" scoped></style>
